<template>
    <!--    <label :for="field.key" class="block" :class="componentClass">-->
    <div :for="field.key" class="flex flex-col w-full mb-3">
        <label v-if="field.translated?.label" class="block text-xs font-bold tracking-wider mb-2 text-gray-500">
            {{ field.translated?.label }}
            <span v-if="isRequired" class="!text-red-500">*</span>
        </label>

        <div v-if="field.key === 'license_plate' || field.key === 'license_plate_confirmation'"
             class="flex items-center gap-2 w-full ring-1 rounded-[12px] px-2 py-1"
             :class="hasError ? 'ring-red-500' : isBelgiumPlate ? 'ring-[rgb(162,37,43)]' : 'ring-gray-200'"
             :style="plateContainerStyle"
             @mousedown.stop
             @click.stop="focusPlateInput($event)">
            <div v-if="plateImg.stars === 'yes'"
                 class="!bg-[rgba(1,46,158,1)] w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 border border-gray-300 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue h-[40px]">
                <img
                    v-if="resolvedImageSrc"
                    :src="resolvedImageSrc"
                    :alt="imageAlt"
                    width="15"
                    height="15"
                    class="py-[2px]"
                />
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>
            <div v-else-if="plateImg.stars === 'no'"
                 class="!bg-[rgba(1,46,158,1)] w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 h-[40px]">
                <img v-if="getFlagByCountryCode" :src="getFlagByCountryCode" width="15" height="15" class="py-[2px]" />
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>
            <div v-else
                 class="!bg-[rgba(1,46,158,1)] w-[32px] p-[2px] rounded-[4px] flex flex-col items-center justify-center flex-shrink-0 border border-gray-300 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue h-[40px]">
                <img
                    v-if="resolvedImageSrc"
                    :src="resolvedImageSrc"
                    :alt="imageAlt"
                    width="15"
                    height="15"
                    class="py-[2px]"
                />
                <p class="!text-white !text-[10px] text-center whitespace-nowrap">{{ plateImg.code }}</p>
            </div>

            <input v-if="!isGermanyOrAustriaPlate"
                   ref="plateInput"
                   :type="getInputType(field.type)"
                   class="focus:ring-0 focus:ring-brand-blue focus:border-brand-blue border-0 outline-none text-lg tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 transition-all w-full min-w-0"
                   maxlength="12"
                   inputmode="text"
                   autocomplete="off"
                   autocapitalize="characters"
                   :name="field.key"
                   :value="stepData[field.key]"
                   @input="onPlateInput($event)"
                   :placeholder="plateImg && plateImg.placeholder ? plateImg.placeholder : field.translated?.placeholder"
            />

            <div v-else
                 class="flex items-center gap-2 w-full min-w-0">
                <input
                    ref="firstSegmentInput"
                    :value="plateSegments.seg1"
                    maxlength="3"
                    inputmode="text"
                    autocomplete="off"
                    autocapitalize="characters"
                    :name="`${field.key}_seg1`"
                    :placeholder="segmentPlaceholders[0]"
                    class="w-1/4 min-w-0 border border-gray-300 text-lg tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg1', $event)"
                />
                <div class="flex flex-col items-center justify-center gap-[4px] flex-shrink-0" aria-hidden="true">
                    <span class="w-[8px] h-[8px] rounded-full bg-[rgb(221,221,221)]"></span>
                    <span class="w-[10px] h-[8px] rounded-full bg-[rgb(221,221,221)]"></span>
                </div>
                <input
                    :value="plateSegments.seg2"
                    :maxlength="segment2Maxlength"
                    inputmode="text"
                    autocomplete="off"
                    autocapitalize="characters"
                    :placeholder="segmentPlaceholders[1]"
                    class="w-1/4 min-w-0 border border-gray-300 text-lg tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg2', $event)"
                />
                <input
                    :value="plateSegments.seg3"
                    :maxlength="isGermanyPlate ? 5 : 6"
                    inputmode="text"
                    autocomplete="off"
                    autocapitalize="characters"
                    :placeholder="segmentPlaceholders[2]"
                    class="w-1/3 min-w-0 border border-gray-300 text-lg tracking-wider uppercase placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all h-[40px]"
                    :class="hasError ? 'ring-red-500' : 'ring-slate-200'"
                    @input="onSegmentInput('seg3', $event)"
                />
            </div>
        </div>
        <div
            v-if="!(field.key === 'license_plate' || field.key === 'license_plate_confirmation')"
            class="relative w-full rounded-[12px]"
            :class="hasError ? 'ring-1 ring-red-500' : ''"
        >
            <!-- Иконка -->
            <img
                v-if="alternateEmail?.src"
                :src="alternateEmail.src"
                :alt="alternateEmail?.alt ?? ''"
                class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none"
            />

            <!-- Input -->
            <input
                :type="getInputType(field.type)"
                v-model="stepData[field.key]"
                :name="field.key"
                :placeholder="field.translated?.placeholder"
                :class="[
                    'block w-full py-4 border rounded-[12px] focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-lg tracking-wider placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 transition-all',
                    alternateEmail?.src ? 'pl-12 pr-4' : 'px-4',
                    hasError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
                ]"
            />
        </div>
        <!--        <p v-if="hasError" class="!text-xs !text-red-500 mt-1">{{ field.translated.label }} is required</p>-->
    </div>

</template>

<script>
import { LICENSE_PLATES } from '../config/licensePlates';
export default {
    name: "TextInput",
    props: {
        field: Object,
        stepData: Object,
        imageSrc: {
            type: [Object, String],
            required: false,
            default: null
        },
        countries: { type: Array, default: () => [] },
        hasError: { type: Boolean, default: false },
        alternateEmail: { type: Object, default: null }
    },
    emits: ['clear-error'],
    data() {
        return {
            plateSegments: {
                seg1: '',
                seg2: '',
                seg3: ''
            }
        };
    },
    methods: {
        filterStandardPlateInput(str) {
            if (str == null || typeof str !== 'string') return '';
            return str.replace(/[^A-Za-z0-9ÄÖÜäöü-]/g, '');
        },
        filterSegmentPlateInput(str) {
            if (str == null || typeof str !== 'string') return '';
            return str.replace(/[^A-Za-z0-9ÄÖÜäöü]/g, '');
        },
        isTruthyExpectedValue(value) {
            return ['1', 'true', 'on', 'yes'].includes(String(value ?? '').trim().toLowerCase());
        },
        isTruthyValue(value) {
            if (value === true || value === 1) return true;
            if (value === false || value === 0 || value == null) return false;
            return this.isTruthyExpectedValue(value);
        },
        onPlateInput(ev) {
            const val = this.filterStandardPlateInput(ev.target?.value ?? '');
            this.stepData[this.field.key] = val;
            if (ev.target && ev.target.value !== val) {
                ev.target.value = val;
            }
            if (this.hasError && val) this.$emit('clear-error', this.field.key);
        },
        onSegmentInput(segment, ev) {
            const val = this.filterSegmentPlateInput(ev.target?.value ?? '');
            this.plateSegments[segment] = val;
            if (ev.target && ev.target.value !== val) {
                ev.target.value = val;
            }
        },
        resolveInputRef(refName) {
            const ref = this.$refs[refName];
            if (!ref) return null;
            return Array.isArray(ref) ? ref[0] : ref;
        },
        focusInputElement(input) {
            if (!input || typeof input.focus !== 'function') return;
            this.$nextTick(() => {
                try {
                    input.focus({ preventScroll: true });
                } catch (_) {
                    input.focus();
                }
            });
        },
        focusPlateInput(event) {
            const target = event?.target;

            if (target && target.tagName === 'INPUT') {
                return;
            }

            if (this.isGermanyOrAustriaPlate) {
                this.focusInputElement(this.resolveInputRef('firstSegmentInput'));
                return;
            }

            this.focusInputElement(this.resolveInputRef('plateInput'));
        },
        syncPlateSegmentsFromStoredValue() {
            if (!this.isLicensePlateField) return;

            const raw = this.stepData[this.field.key];
            if (!raw) {
                this.plateSegments = { seg1: '', seg2: '', seg3: '' };
                return;
            }

            if (this.isGermanyPlate) {
                const dashIdx = raw.indexOf('-');
                if (dashIdx >= 0) {
                    const region = this.filterSegmentPlateInput(raw.slice(0, dashIdx));
                    const rest = this.filterSegmentPlateInput(raw.slice(dashIdx + 1));
                    const m = rest.match(/^([A-Za-zÄÖÜäöü]*)([0-9]*)$/);
                    this.plateSegments.seg1 = region.trim();
                    this.plateSegments.seg2 = m ? m[1] : '';
                    this.plateSegments.seg3 = m ? m[2] : '';
                } else {
                    const cleaned = this.filterSegmentPlateInput(raw);
                    const m = cleaned.match(/^([A-Za-zÄÖÜäöü]+)([A-Za-zÄÖÜäöü]+)([0-9]+)$/);
                    this.plateSegments = m
                        ? { seg1: m[1], seg2: m[2], seg3: m[3] }
                        : { seg1: '', seg2: '', seg3: '' };
                }
                return;
            }

            if (this.isAustriaPlate) {
                const dashIdx = raw.indexOf('-');
                if (dashIdx >= 0) {
                    const rest = this.filterSegmentPlateInput(raw.slice(dashIdx + 1));
                    this.plateSegments.seg1 = this.filterSegmentPlateInput(raw.slice(0, dashIdx)).trim();
                    this.plateSegments.seg2 = rest.slice(0, 5);
                    this.plateSegments.seg3 = rest.slice(5);
                    return;
                }

                const value = this.filterSegmentPlateInput(raw);
                this.plateSegments = {
                    seg1: value.slice(0, 3),
                    seg2: value.slice(3, 8),
                    seg3: value.slice(8, 14),
                };
                return;
            }
        },
        getInputType(type) {
            const typeMap = {
                email_input: "email",
                text_input: "text",
                date_input: "date"
            };
            return typeMap[type] || "text";
        }
    },
    computed: {
        isRequired() {
            const r = this.field.validation_rules;
            if (!r) return false;

            const rules = Array.isArray(r) ? r : [r];

            if (rules.includes('required')) return true;

            for (const rule of rules) {
                if (typeof rule !== 'string' || !rule.startsWith('required_if:')) {
                    continue;
                }
                const [, params = ''] = rule.split(':');
                const [field, expected] = params.split(',');
                const dependentField = String(field || '').split('.').pop();
                if (dependentField === 'need_an_invoice' && this.isTruthyExpectedValue(expected)) {
                    return this.isTruthyValue(this.stepData?.need_an_invoice);
                }
            }

            return false;
        },

        isLicensePlateField() {
            return this.field.key === 'license_plate' || this.field.key === 'license_plate_confirmation';
        },

        isGermanyPlate() {
            return this.isLicensePlateField && this.stepData?.plate_country === 'DE';
        },

        isAustriaPlate() {
            return this.isLicensePlateField && this.stepData?.plate_country === 'AT';
        },

        isGermanyOrAustriaPlate() {
            return this.isGermanyPlate || this.isAustriaPlate;
        },

        isBelgiumPlate() {
            return this.stepData?.plate_country === 'BE';
        },

        isNetherlandsPlate() {
            return this.stepData?.plate_country === 'NL';
        },

        segmentPlaceholders() {
            if (this.isGermanyPlate) return ['B', 'AB', '1234'];
            if (this.isAustriaPlate) return ['122', 'ABCDE', '55645'];
            return ['', '', ''];
        },

        segment2Maxlength() {
            return this.isAustriaPlate ? 5 : 3;
        },

        unifiedPlateValue() {
            if (!this.isLicensePlateField) return null;

            const { seg1, seg2, seg3 } = this.plateSegments;
            if (this.isGermanyPlate) {
                return (seg1 ? `${seg1}-` : '') + seg2 + seg3;
            }
            if (this.isAustriaPlate) {
                return ((seg1 ? `${seg1}-` : '') + seg2 + seg3).toUpperCase();
            }
            return '';
        },

        plateContainerStyle() {
            if (this.isNetherlandsPlate && this.plateImg?.color) {
                return { backgroundColor: this.plateImg.color };
            }
            return {};
        },

        plateImg() {
            const code = this.stepData?.plate_country;
            return LICENSE_PLATES[code] ?? { stars: 'no', code: '', placeholder: '', color: 'rgba(244,244,244,1)' };
        },

        resolvedImageSrc() {
            if (!this.imageSrc) return null;
            if (typeof this.imageSrc === 'string') return this.imageSrc;
            return this.imageSrc.src ?? this.imageSrc.url ?? null;
        },

        imageAlt() {
            if (!this.imageSrc || typeof this.imageSrc !== 'object') return 'Vignette Image';
            return this.imageSrc.translated?.alt ?? 'Vignette Image';
        },

        getFlagByCountryCode() {
            // console.log("StepDataText", this.stepData?.plate_country);
            // console.log("Countries Data", this.countries);

            if (this.stepData?.plate_country && this.countries) {
                const countryCode = this.stepData?.plate_country;

                const country = this.countries.find(item => item.code === countryCode);
                // console.log("CountryCode", countryCode);

                if (country) {
                    const countryFlag = country.flag;
                    // this.flagForCountry = countryFlag;
                    // console.log(countryFlag);
                    return countryFlag;
                } else {
                    return null;
                }
            }
            return null;
        }
    },
    watch: {
        unifiedPlateValue(val) {
            if (!this.isLicensePlateField || val == null) return;

            const out = this.isGermanyPlate ? val : val.toUpperCase();
            this.stepData[this.field.key] = out;
            if (this.hasError && out) {
                this.$emit('clear-error', this.field.key);
            }
        },
        isGermanyOrAustriaPlate: {
            handler(isSegmented, wasSegmented) {
                if (!this.isLicensePlateField) return;

                if (isSegmented) {
                    this.syncPlateSegmentsFromStoredValue();
                    return;
                }
                if (wasSegmented) {
                    this.plateSegments = { seg1: '', seg2: '', seg3: '' };
                }
            },
            immediate: true
        },
        'stepData.plate_country'() {
            if (!this.isLicensePlateField || !this.isGermanyOrAustriaPlate) return;
            this.syncPlateSegmentsFromStoredValue();
        }
    },

    created() {
        this.$watch(
            () => this.stepData[this.field.key],
            (val) => {
                if (this.hasError && val?.toString().trim() !== '') {
                    this.$emit('clear-error', this.field.key);
                }
            }
        );
    }

};
</script>
